import { ComputeFunnelMetricsResponse } from '@fullstory/proto/fs/services/alexa';
import { Duration, int64, timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { AppCacheStatus } from '@fullstory/redux-util';
import { EVERYONE_SEGMENT } from '../segmentService';
import { addDays } from 'date-fns';
import { uniqueId } from 'lodash';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

export const COMPUTE_FUNNEL_METRIC_RESPONSE: ComputeFunnelMetricsResponse = {
  data: {
    steps: [],
  },
  computedAt: Timestamp.now(),
  backgroundRefId: '',
  sampleResults: null,
};

export const COMPUTE_FUNNEL_METRIC_RESPONSE_WITH_STEPS: ComputeFunnelMetricsResponse = {
  data: {
    steps: [
      {
        table: [
          {
            startTime: null,
            endTime: null,
            groups: [],
            value: 192522,
            timeToComplete: [],
            correlatedEventValues: [],
            isPartialPeriod: false,
          },
        ],
        numGroups: '0' as int64,
      },
    ],
  },
  computedAt: Timestamp.now(),
  backgroundRefId: '',
  sampleResults: null,
};

const mockGroupTable = (value: number, numberOfGroups = 1) => {
  const table = [
    {
      startTime: '2020-09-14T04:00:00Z' as timestamp,
      endTime: '2020-10-13T14:55:00Z' as timestamp,
      groups: [{ groupId: 'Everyone', fallback: false }],
      value,
      timeToComplete: [],
      correlatedEventValues: [],
      isPartialPeriod: false,
    },
  ];
  for (let i = 1; i < numberOfGroups && i < 4; i++) {
    table.push({
      startTime: '2020-09-14T04:00:00Z' as timestamp,
      endTime: '2020-10-13T14:55:00Z' as timestamp,
      groups: [
        {
          groupId: `group-${i}`,
          fallback: false,
        },
      ],
      value,
      timeToComplete: [],
      correlatedEventValues: [],
      isPartialPeriod: false,
    });
  }
  return table;
};

export const mockComputeFunnelMetricsResponse = (
  numberOfGroups = 1,
): ComputeFunnelMetricsResponse => {
  const step1Table = mockGroupTable(537, numberOfGroups);
  const step2Table = mockGroupTable(452, numberOfGroups);
  const step3Table = mockGroupTable(438, numberOfGroups);

  return {
    data: {
      steps: [
        {
          table: [...step1Table],
          numGroups: int64.fromNumber(numberOfGroups),
        },
        {
          table: [...step2Table],
          numGroups: int64.fromNumber(numberOfGroups),
        },
        {
          table: [...step3Table],
          numGroups: int64.fromNumber(numberOfGroups),
        },
      ],
    },
    computedAt: Timestamp.now(),
    backgroundRefId: '',
    sampleResults: null,
  };
};

const makeTableForStep = (
  startTime: timestamp | null,
  endTime: timestamp | null,
  value: number,
  group: string,
) => ({
  startTime,
  endTime,
  groups: [{ groupId: group, fallback: false }],
  value,
  timeToComplete: [],
  correlatedEventValues: [],
  id: uniqueId(),
  isPartialPeriod: false,
});

/*
This method makes a step table for funnels that has an intuitive conversion to make our unit tests more understandable. The initial value is divided by 2
on each row so we don't end up with conversion numbers that are long decimals. Groups are made in the case of being even and odd for testing.
*/
const makeStepForMetricResponse = (
  numTableRows: number,
  initialValue: number,
  startDateParam: Date = new Date('2023-06-01T04:00:00Z'),
) => {
  const table = [];
  let startDate = startDateParam;
  let endDate = addDays(startDate, 1);
  let value = initialValue;
  for (let i = 0; i < numTableRows; i++) {
    table.push(
      makeTableForStep(
        Timestamp.fromDate(startDate),
        Timestamp.fromDate(endDate),
        value,
        i % 2 === 0 ? 'Chrome' : 'Firefox',
      ),
    );
    value /= 2;
    startDate = addDays(startDate, 1);
    endDate = addDays(endDate, 1);
  }

  return {
    table,
    numGroups: int64.fromNumber(0),
    id: uniqueId(),
  };
};

export const mockFunnelMetricResponse = [
  {
    funnelMetrics: {
      data: {
        steps: [makeStepForMetricResponse(3, 1000), makeStepForMetricResponse(3, 100)],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: false,
  },
];

export const mockFunnelMetricResponseCompareToPast = [
  {
    funnelMetrics: {
      data: {
        steps: [makeStepForMetricResponse(3, 1000), makeStepForMetricResponse(3, 100)],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: false,
  },
  {
    funnelMetrics: {
      data: {
        steps: [
          makeStepForMetricResponse(3, 1000, new Date('2023-05-31T04:00:00Z')),
          makeStepForMetricResponse(3, 200, new Date('2023-05-31T04:00:00Z')),
        ],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: true,
  },
];

export const mockFunnelMetricResponseManyStepsCompareToPast = [
  {
    funnelMetrics: {
      data: {
        steps: [
          makeStepForMetricResponse(3, 1000),
          makeStepForMetricResponse(3, 100),
          makeStepForMetricResponse(3, 100000),
          makeStepForMetricResponse(3, 5000),
          makeStepForMetricResponse(3, 70000),
        ],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: false,
  },
  {
    funnelMetrics: {
      data: {
        steps: [
          makeStepForMetricResponse(3, 1000, new Date('2023-05-31T04:00:00Z')),
          makeStepForMetricResponse(3, 200, new Date('2023-05-31T04:00:00Z')),
          makeStepForMetricResponse(3, 1, new Date('2023-05-31T04:00:00Z')),
          makeStepForMetricResponse(3, 999999, new Date('2023-05-31T04:00:00Z')),
          makeStepForMetricResponse(3, 923423, new Date('2023-05-31T04:00:00Z')),
        ],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: true,
  },
];

export const mockFunnelMetricResponseManySteps = [
  {
    funnelMetrics: {
      data: {
        steps: [
          makeStepForMetricResponse(3, 1000),
          makeStepForMetricResponse(3, 100),
          makeStepForMetricResponse(3, 100000),
          makeStepForMetricResponse(3, 5000),
          makeStepForMetricResponse(3, 70000),
        ],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: false,
  },
];

export const mockFunnelMetricMultiSegmentResponse = [
  {
    funnelMetrics: {
      data: {
        steps: [makeStepForMetricResponse(3, 1000), makeStepForMetricResponse(3, 100)],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'everyone',
    isPast: false,
  },
  {
    funnelMetrics: {
      data: {
        steps: [makeStepForMetricResponse(3, 5000), makeStepForMetricResponse(3, 500)],
      },
      computedAt: Timestamp.fromDate(new Date('2023-08-22T20:18:25.541972456Z')),
      backgroundRefId: '',
      sampleResults: null,
    },
    status: AppCacheStatus.Fresh,
    safeMessage: null,
    segmentId: 'nobody',
    isPast: false,
  },
];

export const mockFunnelMetricSegment = [
  EVERYONE_SEGMENT,
  {
    segment: {
      segment: {
        id: 'nobody',
        name: 'Nobody',
        description: '',
        segmentDefinition: {
          userProperties: [],
          excludeUserProperties: [],
          behaviors: [],
          excludeBehaviors: [],
          constraints: {
            inOrder: false,
            inSameSession: false,
            platform: 0,
            timeRange: {
              startTime: 'NOW/DAY-29DAY',
              endTime: 'NOW/DAY+1DAY',
            },
            completeSessions: false,
            withinTime: Duration.fromSeconds(0),
            eventThresholds: [],
          },
          excludeConstraints: null,
        },
      },
      segmentMetadata: {
        canned: true,
      },
      metadata: {
        lastUpdated: null,
        lastUpdatedBy: 'system',
        created: null,
        createdBy: 'system',
        archived: false,
        visibility: Visibility.PUBLIC,
        version: int64.fromNumber(0),
        sequentialVersion: int64.fromNumber(0),
      },
    },
    starred: true,
    lastViewed: null,
  },
];
